import { axiosGet, axiosPost, axiosPatch } from './axiosService';
import { isExpired, decodeToken } from 'react-jwt';
import { LocalStorageKey } from '../constants/localStorageKeys';
import { UtilitiesService } from './utilitiesService';
import { Roles } from '../constants/roles';
import { Toaster } from '../shared/components/common/toaster';

const authURL = 'authentication/';
const loginURL = authURL + 'login/';
const forgotPasswordURL = authURL + 'forgotpassword/';
const resetPasswordURL = authURL + 'resetpassword/';
const adminAuthURL = authURL + 'EnableAuthenticator';
const utilitiesService = new UtilitiesService();

// The API issues admins a refresh token, but admin sessions do not renew — drop it before anything is stored.
const withoutRefreshToken = ({
	refreshToken,
	accessTokenExpiresAt,
	refreshTokenExpiresAt,
	...rest
}) => rest;

export const login = async (credentials) => {
	try {
		const resp = await axiosPost(credentials, loginURL, {
			hide_success_toaster: true,
		});
		if (resp.data && resp.data.token) {
			if (isAdmin(resp.data)) {
				saveLoginInfo(withoutRefreshToken(resp.data));
				saveToken(resp.data.token);
				const user = getExtractedUserFromToken();
				setUser(user);
				return resp;
			} else {
				return 'notAdmin';
			}
		}
		return null;
	} catch (e) {
		throw e;
	}
};

export const forgotPassword = (form) => {
	return axiosPost(form, forgotPasswordURL, { hide_toaster: 'true' });
};

export const resetPassword = (form) => {
	return axiosPatch(resetPasswordURL, form, { hide_toaster: 'true' });
};

export const getAdminAuthQRCode = () => {
	return axiosGet(null, adminAuthURL);
};

export const submitAdminOTPVerificationCode = (form) => {
	return axiosPost(form, adminAuthURL);
};

export const verifyAdminAuth = (form) => {
	return axiosPost(form, 'otp/Verify/mfa', {});
};

const mapTokenPayloadToUser = (tokenPayload) => {
	const isApproved = tokenPayload.approved === 'True';
	let OtpVerified = true;
	if (tokenPayload.hasOwnProperty('OtpVerified')) {
		OtpVerified = tokenPayload.OtpVerified === 'True';
	}

	return {
		nameid: tokenPayload.nameid,
		email: tokenPayload.email,
		unique_name: tokenPayload.unique_name,
		role: tokenPayload.role,
		isApproved: isApproved,
		status: tokenPayload.status,
		OtpVerified: OtpVerified,
	};
};

export const isAdmin = (user) => {
	if (user) {
		return !!user.roles.find(
			(role) =>
				role === Roles.AccountManager ||
				role === Roles.SuperAdmin ||
				role === Roles.Compliance ||
				role === Roles.SEOManager ||
				role === Roles.Credit
		);
	}
	return false;
};

export const setUser = (user) => {
	utilitiesService.setLocalStorage({
		item: LocalStorageKey.users,
		value: JSON.stringify(user),
	});
};

export const loginUsingToken = (token) => {
	const isTokenExpired = isExpired(token);
	if (isTokenExpired) {
		return null;
	}

	saveToken(token);
	const user = getExtractedUserFromToken();
	setUser(user);
};

export const getExtractedUserFromToken = () => {
	const token = getToken();
	const isTokenExpired = isExpired(token);
	if (isTokenExpired) {
		return null;
	}

	const tokenPayload = decodeToken(token);
	const user = mapTokenPayloadToUser(tokenPayload);
	if (!user?.isApproved) {
		Toaster({ msg: 'User is not approved!' });
		return null;
	}
	return user;
};

export const getUser = () => {
	return utilitiesService.getLocalStorage({ item: LocalStorageKey.users })
		? JSON.parse(
			utilitiesService.getLocalStorage({ item: LocalStorageKey.users })
		)
		: null;
};

export const getFirstName = () => {
	if (userName()) {
		return userName()?.split(' ')[0] || '';
	}
	return '';
};

export const userName = () => {
	if (getUser()) {
		return getUser()?.unique_name;
	}
};

export const saveToken = (token) => {
	utilitiesService.setLocalStorage({
		item: LocalStorageKey.token,
		value: token,
	});
};

export const saveLoginInfo = (info) => {
	utilitiesService.setLocalStorage({
		item: LocalStorageKey.loginInfo,
		value: JSON.stringify(info),
	});
};

export const getToken = () => {
	return utilitiesService.getLocalStorage({ item: LocalStorageKey.token });
};

export const getLoginInfo = () => {
	let info = utilitiesService.getLocalStorage({
		item: LocalStorageKey.loginInfo,
	});
	return info ? JSON.parse(info) : null;
};

export const ClearUtilitiesStorage = () => {
	Object.keys(localStorage).forEach((key) => {
		if (
			key.includes(LocalStorageKey.token) ||
			key.includes(LocalStorageKey.users) ||
			key.includes(LocalStorageKey.transactionCount)
		) {
		} else {
			localStorage.removeItem(key);
		}
	});
};

export const Logout = () => {
	utilitiesService.removeLocalStorage({ item: LocalStorageKey.users });
	utilitiesService.removeLocalStorage({ item: LocalStorageKey.token });
	utilitiesService.removeLocalStorage({
		item: LocalStorageKey.transactionCount,
	});
	utilitiesService.removeLocalStorage({
		item: LocalStorageKey.investorsCollections,
	});
	utilitiesService.removeLocalStorage({
		item: LocalStorageKey.borrowersCollections,
	});
	utilitiesService.removeLocalStorage({
		item: LocalStorageKey.loadAllCollection,
	});
	utilitiesService.removeLocalStorage({
		item: LocalStorageKey.loginInfo,
	});
	utilitiesService.removeLocalStorage({
		item: LocalStorageKey.chatConversation,
	});
	utilitiesService.removeLocalStorage({
		item: LocalStorageKey.chatPanel,
	});
	localStorage.setItem("cancelCount", "0");
	localStorage.setItem('isCancelAPI', "0");
	const existingAlert = document.querySelector('.overlay-alert');
	if (existingAlert) {
		document.body.removeChild(existingAlert);
	}
};

export const changeEmail = (id, email) => {
	return axiosPatch(`${authURL}${id}/change-email`, { email: email });
}